import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/seo/seo-head";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, PiggyBank } from "lucide-react";
import { showToast } from "@/lib/toast";
import { Skeleton } from "@/components/shared/skeleton";
import { useCurrency } from "@/hooks/use-currency";
import { useLanguage } from "@/contexts/language-context";

interface Income {
  id: string;
  sourceName: string;
  amount: number;
  date: string;
}

interface Expense {
  id: string;
  category: string;
  amount: number;
  date: string;
}

export function BudgetPage() {
  const { formatCurrency } = useCurrency();
  const { t, language } = useLanguage();
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  
  // Pagination state
  const [incomePage, setIncomePage] = useState(1);
  const [expensePage, setExpensePage] = useState(1);
  const [incomePagination, setIncomePagination] = useState<{ totalPages: number; totalItems: number } | null>(null);
  const [expensePagination, setExpensePagination] = useState<{ totalPages: number; totalItems: number } | null>(null);
  const itemsPerPage = 10;

  // Income form state
  const [incomeForm, setIncomeForm] = useState({
    sourceName: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
  });

  // Expense form state
  const [expenseForm, setExpenseForm] = useState({
    category: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchData();
  }, [incomePage, expensePage]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Fetch data with pagination
      const [incomesResponse, expensesResponse] = await Promise.all([
        api.get<{ incomes: Income[]; pagination?: { page: number; limit: number; totalPages: number; total: number } }>(
          `/income?page=${incomePage}&limit=${itemsPerPage}`
        ).catch((err) => {
          console.error("Failed to fetch incomes:", err);
          showToast.error("Failed to fetch income data");
          return { incomes: [], pagination: undefined };
        }),
        api.get<{ expenses: Expense[]; pagination?: { page: number; limit: number; totalPages: number; total: number } }>(
          `/expenses?page=${expensePage}&limit=${itemsPerPage}`
        ).catch((err) => {
          console.error("Failed to fetch expenses:", err);
          showToast.error("Failed to fetch expense data");
          return { expenses: [], pagination: undefined };
        }),
      ]);
      
      setIncomes(incomesResponse.incomes || []);
      setExpenses(expensesResponse.expenses || []);
      
      // Set pagination info
      if (incomesResponse.pagination) {
        setIncomePagination({
          totalPages: incomesResponse.pagination.totalPages,
          totalItems: incomesResponse.pagination.total || 0,
        });
      } else {
        setIncomePagination(null);
      }
      
      if (expensesResponse.pagination) {
        setExpensePagination({
          totalPages: expensesResponse.pagination.totalPages,
          totalItems: expensesResponse.pagination.total || 0,
        });
      } else {
        setExpensePagination(null);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      showToast.error(error instanceof Error ? error.message : "Failed to fetch data");
      setIncomes([]);
      setExpenses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIncomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingIncome) {
        await api.put(`/income/${editingIncome.id}`, {
          sourceName: incomeForm.sourceName,
          amount: parseFloat(incomeForm.amount),
          date: incomeForm.date,
        });
      } else {
        await api.post("/income", {
          sourceName: incomeForm.sourceName,
          amount: parseFloat(incomeForm.amount),
          date: incomeForm.date,
        });
      }
      setShowIncomeForm(false);
      setEditingIncome(null);
      setIncomeForm({ sourceName: "", amount: "", date: new Date().toISOString().split("T")[0] });
      showToast.success(editingIncome ? t("budget.incomeUpdated") : t("budget.incomeAdded"));
      // Reset to first page and refetch
      setIncomePage(1);
      fetchData();
    } catch (error) {
      console.error("Failed to save income:", error);
      showToast.error(error instanceof Error ? error.message : "Failed to save income");
    }
  };

  const handleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, {
          category: expenseForm.category,
          amount: parseFloat(expenseForm.amount),
          date: expenseForm.date,
        });
      } else {
        await api.post("/expenses", {
          category: expenseForm.category,
          amount: parseFloat(expenseForm.amount),
          date: expenseForm.date,
        });
      }
      setShowExpenseForm(false);
      setEditingExpense(null);
      setExpenseForm({ category: "", amount: "", date: new Date().toISOString().split("T")[0] });
      showToast.success(editingExpense ? t("budget.expenseUpdated") : t("budget.expenseAdded"));
      // Reset to first page and refetch
      setExpensePage(1);
      fetchData();
    } catch (error) {
      console.error("Failed to save expense:", error);
      showToast.error(error instanceof Error ? error.message : "Failed to save expense");
    }
  };

  const handleDeleteIncome = async (id: string) => {
    if (!confirm(t("budget.deleteIncomeConfirm"))) return;
    try {
      await api.delete(`/income/${id}`);
      showToast.success(t("budget.incomeDeleted"));
      // If this was the last item on the page and not page 1, go to previous page
      if (incomes.length === 1 && incomePage > 1) {
        setIncomePage(incomePage - 1);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error("Failed to delete income:", error);
      showToast.error("Failed to delete income");
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (!confirm(t("budget.deleteExpenseConfirm"))) return;
    try {
      await api.delete(`/expenses/${id}`);
      showToast.success(t("budget.expenseDeleted"));
      // If this was the last item on the page and not page 1, go to previous page
      if (expenses.length === 1 && expensePage > 1) {
        setExpensePage(expensePage - 1);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error("Failed to delete expense:", error);
      showToast.error("Failed to delete expense");
    }
  };

  const handleEditIncome = (income: Income) => {
    setEditingIncome(income);
    setIncomeForm({
      sourceName: income.sourceName,
      amount: income.amount.toString(),
      date: new Date(income.date).toISOString().split("T")[0],
    });
    setShowIncomeForm(true);
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      category: expense.category,
      amount: expense.amount.toString(),
      date: new Date(expense.date).toISOString().split("T")[0],
    });
    setShowExpenseForm(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === "id" ? "id-ID" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalIncome = incomes.reduce((sum, income) => sum + income.amount, 0);
  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const savings = totalIncome - totalExpenses;


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Budget - Personal Finance App"
        description="Manage your income and expenses. Track your monthly budget, view spending trends, and analyze your financial transactions."
        keywords="budget, income, expenses, spending tracker, financial transactions"
        canonicalUrl="/budget"
      />
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("budget.title")}</h1>
        <p className="text-muted-foreground">
          {t("budget.subtitle")}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{t("budget.totalIncome")}</p>
                <p className="text-lg md:text-xl font-bold text-primary tabular-nums font-mono truncate">{formatCurrency(totalIncome)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                <TrendingDown className="h-4 w-4 text-destructive" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{t("budget.totalExpenses")}</p>
                <p className="text-lg md:text-xl font-bold text-destructive tabular-nums font-mono truncate">{formatCurrency(totalExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2 md:col-span-1">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-chart-2/10 flex items-center justify-center shrink-0">
                <PiggyBank className="h-4 w-4 text-chart-2" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{t("budget.savings")}</p>
                <p className={`text-lg md:text-xl font-bold tabular-nums font-mono truncate ${savings >= 0 ? "text-primary" : "text-destructive"}`}>
                  {formatCurrency(savings)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>


      <div className="grid gap-6 md:grid-cols-2">
        {/* Income Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t("budget.income")}</CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setEditingIncome(null);
                setIncomeForm({ sourceName: "", amount: "", date: new Date().toISOString().split("T")[0] });
                setShowIncomeForm(!showIncomeForm);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("budget.addIncome")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {showIncomeForm && (
              <form onSubmit={handleIncomeSubmit} className="space-y-3 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="income-source">{t("budget.sourceName")}</Label>
                  <Input
                    id="income-source"
                    value={incomeForm.sourceName}
                    onChange={(e) => setIncomeForm({ ...incomeForm, sourceName: e.target.value })}
                    placeholder="e.g., Salary, Freelance"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="income-amount">{t("budget.amount")}</Label>
                  <Input
                    id="income-amount"
                    type="number"
                    step="0.01"
                    value={incomeForm.amount}
                    onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="income-date">{t("budget.date")}</Label>
                  <Input
                    id="income-date"
                    type="date"
                    value={incomeForm.date}
                    onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm">
                    {editingIncome ? t("budget.update") : t("common.add")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowIncomeForm(false);
                      setEditingIncome(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </form>
            )}

            <div className="space-y-2">
              {incomes.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">{t("budget.noIncomeRecords")}</p>
                </div>
              ) : (
                <>
                  {incomes.map((income) => (
                    <div
                      key={income.id}
                      className="flex items-center justify-between p-3 md:p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <TrendingUp className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{income.sourceName}</p>
                          <p className="text-xs md:text-sm text-muted-foreground">{formatDate(income.date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 md:gap-2 shrink-0">
                        <span className="font-medium tabular-nums font-mono text-sm md:text-base">{formatCurrency(income.amount)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditIncome(income)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteIncome(income.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Income Pagination */}
                  {incomePagination && incomePagination.totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        {t("budget.showing")} {(incomePage - 1) * itemsPerPage + 1} {t("budget.to")}{" "}
                        {Math.min(incomePage * itemsPerPage, incomePagination.totalItems)} {t("budget.of")}{" "}
                        {incomePagination.totalItems} {t("budget.entries")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIncomePage((prev) => Math.max(1, prev - 1))}
                          disabled={incomePage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          {t("budget.previous")}
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, incomePagination.totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (incomePagination.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (incomePage <= 3) {
                              pageNum = i + 1;
                            } else if (incomePage >= incomePagination.totalPages - 2) {
                              pageNum = incomePagination.totalPages - 4 + i;
                            } else {
                              pageNum = incomePage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={incomePage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setIncomePage(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIncomePage((prev) => Math.min(incomePagination.totalPages, prev + 1))}
                          disabled={incomePage === incomePagination.totalPages}
                        >
                          {t("budget.next")}
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Expenses Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t("budget.expenses")}</CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setEditingExpense(null);
                setExpenseForm({ category: "", amount: "", date: new Date().toISOString().split("T")[0] });
                setShowExpenseForm(!showExpenseForm);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("budget.addExpense")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {showExpenseForm && (
              <form onSubmit={handleExpenseSubmit} className="space-y-3 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="expense-category">{t("budget.category")}</Label>
                  <Input
                    id="expense-category"
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                    placeholder="e.g., Food, Transport, Housing"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expense-amount">{t("budget.amount")}</Label>
                  <Input
                    id="expense-amount"
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expense-date">{t("budget.date")}</Label>
                  <Input
                    id="expense-date"
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm">
                    {editingExpense ? t("budget.update") : t("common.add")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowExpenseForm(false);
                      setEditingExpense(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </form>
            )}

            <div className="space-y-2">
              {expenses.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingDown className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-1">{t("budget.noExpenseRecords")}</p>
                  <p className="text-xs text-muted-foreground">{t("budget.addExpense")}</p>
                </div>
              ) : (
                <>
                  {expenses.map((expense) => (
                    <div
                      key={expense.id}
                      className="flex items-center justify-between p-3 md:p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                        <div className="h-8 w-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                          <TrendingDown className="h-4 w-4 text-destructive" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{expense.category}</p>
                          <p className="text-xs md:text-sm text-muted-foreground">{formatDate(expense.date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 md:gap-2 shrink-0">
                        <span className="font-medium tabular-nums font-mono text-sm md:text-base">{formatCurrency(expense.amount)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditExpense(expense)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteExpense(expense.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Expense Pagination */}
                  {expensePagination && expensePagination.totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        {t("budget.showing")} {(expensePage - 1) * itemsPerPage + 1} {t("budget.to")}{" "}
                        {Math.min(expensePage * itemsPerPage, expensePagination.totalItems)} {t("budget.of")}{" "}
                        {expensePagination.totalItems} {t("budget.entries")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpensePage((prev) => Math.max(1, prev - 1))}
                          disabled={expensePage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          {t("budget.previous")}
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, expensePagination.totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (expensePagination.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (expensePage <= 3) {
                              pageNum = i + 1;
                            } else if (expensePage >= expensePagination.totalPages - 2) {
                              pageNum = expensePagination.totalPages - 4 + i;
                            } else {
                              pageNum = expensePage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={expensePage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setExpensePage(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpensePage((prev) => Math.min(expensePagination.totalPages, prev + 1))}
                          disabled={expensePage === expensePagination.totalPages}
                        >
                          {t("budget.next")}
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
}
